/**
 * CurrencyChooserModal.tsx
 * Advanced currency selection modal with search and filtering capabilities
 * 
 * Features:
 * - Rich currency database with symbols and descriptions
 * - Real-time search across code, name, and keywords
 * - Filter by currency type (fiat, stablecoin, cryptocurrency)
 * - Visual indicators for currency types
 * - Responsive design with HeroUI components
 */

import React, { useState, useMemo } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Chip, Card, CardBody } from '@enfomentis/ui';
import type { CurrencyType } from '@/types/shared/Currency';
import { searchCurrencies } from '@/utils';
import { CURRENCY_DATABASE } from '@/constants';

/**
 * Props for the CurrencyChooserModal component
 */
interface CurrencyChooserModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  
  /** Handler for closing the modal */
  onClose: () => void;
  
  /** Currently selected currency code */
  selectedCurrency?: string;
  
  /** Handler for currency selection */
  onCurrencySelect: (currencyCode: string) => void;
  
  /** Optional title override */
  title?: string;
}

/**
 * Currency type filter options
 */
const CURRENCY_TYPE_FILTERS: { type: CurrencyType | 'all'; label: string; color: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' }[] = [
  { type: 'all', label: 'All', color: 'default' },
  { type: 'fiat', label: 'Fiat', color: 'primary' },
  { type: 'stablecoin', label: 'Stablecoin', color: 'success' },
  { type: 'cryptocurrency', label: 'Crypto', color: 'warning' },
];

/**
 * Visual mapping for currency types — badge classes, dot color, and display label
 */
const CURRENCY_TYPE_VISUALS: Record<CurrencyType, { badgeClasses: string; dotColor: string; label: string }> = {
  fiat: { badgeClasses: 'bg-primary/20 text-primary', dotColor: 'text-primary', label: 'Fiat' },
  stablecoin: { badgeClasses: 'bg-success/20 text-success', dotColor: 'text-success', label: 'Stablecoin' },
  cryptocurrency: { badgeClasses: 'bg-warning/20 text-warning', dotColor: 'text-warning', label: 'Crypto' },
};

/**
 * Currency chooser modal component
 */
export const CurrencyChooserModal: React.FC<CurrencyChooserModalProps> = ({
  isOpen,
  onClose,
  selectedCurrency,
  onCurrencySelect,
  title = "Choose Currency"
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<CurrencyType | 'all'>('all');

  /**
   * Filtered currencies based on search and type filter
   */
  const filteredCurrencies = useMemo(() => {
    let currencies = searchCurrencies(searchQuery, CURRENCY_DATABASE);
    
    if (typeFilter !== 'all') {
      currencies = currencies.filter(currency => currency.type === typeFilter);
    }
    
    // Sort by: selected first, then by type, then alphabetically
    return currencies.sort((a, b) => {
      // Selected currency first
      if (a.code === selectedCurrency) return -1;
      if (b.code === selectedCurrency) return 1;
      
      // Then by type priority: fiat -> stablecoin -> crypto
      const typeOrder = { fiat: 0, stablecoin: 1, cryptocurrency: 2 };
      const typeComparison = typeOrder[a.type] - typeOrder[b.type];
      if (typeComparison !== 0) return typeComparison;
      
      // Finally alphabetically
      return a.code.localeCompare(b.code);
    });
  }, [searchQuery, typeFilter, selectedCurrency]);

  /**
   * Handle currency selection and close modal
   */
  const handleSelect = (currencyCode: string): void => {
    onCurrencySelect(currencyCode);
    onClose();
  };

  /**
   * Clear search and filters
   */
  const clearFilters = (): void => {
    setSearchQuery('');
    setTypeFilter('all');
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={(open) => !open && onClose()}
      size="2xl"
      placement="center"
      scrollBehavior="inside"
      shouldBlockScroll={false}
      disableAnimation
      classNames={{ 
        wrapper: "items-start h-auto", 
        base: "my-auto" 
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-xl font-semibold">{title}</h3>
              <p className="text-sm text-text-secondary">
                Search by currency code, name, or keywords
              </p>
            </ModalHeader>
            
            <ModalBody className="space-y-4">
              {/* Search Input */}
              <Input
                placeholder="Search currencies... (e.g., USD, Dollar, Bitcoin)"
                value={searchQuery}
                onValueChange={setSearchQuery}
                classNames={{ input: 'text-base' }}
                startContent={
                  <div className="text-text-secondary">🔍</div>
                }
                isClearable
                onClear={() => setSearchQuery('')}
              />
              
              {/* Type Filter Chips */}
              <div className="flex flex-wrap gap-2">
                {CURRENCY_TYPE_FILTERS.map((filter) => (
                  <Chip
                    key={filter.type}
                    color={typeFilter === filter.type ? filter.color : 'default'}
                    variant={typeFilter === filter.type ? 'solid' : 'bordered'}
                    className="cursor-pointer"
                    onClick={() => setTypeFilter(filter.type)}
                  >
                    {filter.label}
                  </Chip>
                ))}
                
                {(searchQuery || typeFilter !== 'all') && (
                  <Button
                    size="sm"
                    variant="light"
                    onClick={clearFilters}
                    className="ml-2"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
              
              {/* Results Count */}
              <div className="text-sm text-text-secondary">
                {filteredCurrencies.length} currencies found
                {typeFilter !== 'all' && ` (${typeFilter} only)`}
              </div>
              
              {/* Currency List */}
              <div className="px-1 space-y-2 max-h-96 overflow-y-auto">
                {filteredCurrencies.length > 0 ? (
                  filteredCurrencies.map((currency) => (
                    <Card
                      shadow='none'
                      key={currency.code}
                      isPressable
                      isHoverable
                      className={`border-1 border-default-200 w-full cursor-pointer transition-colors ${
                        currency.code === selectedCurrency
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-content2'
                      }`}
                      onClick={() => handleSelect(currency.code)}
                    >
                      <CardBody className="p-3">
                        <div className="flex items-center gap-3">
                          {/* Currency Symbol Badge (colored by type) */}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${CURRENCY_TYPE_VISUALS[currency.type].badgeClasses}`}>
                            {currency.symbol || currency.code.slice(0, 2)}
                          </div>

                          {/* Currency Code + Name */}
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold">{currency.code}</div>
                            <div className="text-sm text-text-secondary truncate">
                              {currency.name}
                            </div>
                          </div>

                          {/* Type Indicator (centered vertically) */}
                          <span className={`text-xs shrink-0 ${CURRENCY_TYPE_VISUALS[currency.type].dotColor}`}>
                            ● {CURRENCY_TYPE_VISUALS[currency.type].label}
                          </span>

                          {/* Checkmark Slot (fixed width, always present for alignment) */}
                          <div className="w-6 shrink-0 text-center">
                            {currency.code === selectedCurrency && (
                              <span className="text-primary">✓</span>
                            )}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  ))
                ) : (
                  <div className="text-center py-8 text-text-secondary">
                    <div className="text-4xl mb-2">🔍</div>
                    <div className="text-lg mb-1">No currencies found</div>
                    <div className="text-sm">Try different search terms or clear filters</div>
                  </div>
                )}
              </div>
            </ModalBody>
            
            <ModalFooter>
              <Button
                variant="light"
                onPress={onClose}
              >
                Cancel
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};